import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../endpoints';
import { LoadNature } from '../materials/loadNatures';
import { WasteCode } from '../materials/wasteCodes';
import { Site } from '../planning';
import { Quarry } from '../sites/quarries';
import { TruckCompany } from '../materials/trucksCompanies';

export interface DailyWaste {
    _id: string;
    activityId: string;
    loadNatureId: LoadNature;
    wasteCodeId?: WasteCode;
    siteId?: Site;
    quarryId?: Quarry;
    companyId?: TruckCompany;
    direction: number;
    quantity?: number;
    unitPrice?: number;
    price?: number;
    updatedAt?: string;
    createdAt?: string;
}

export interface DailyWastesResponse {
    message: string;
    data: DailyWaste[];
}

export interface DailyWasteResponse {
    message: string;
    data: DailyWaste;
}

export interface GetDailyWastesQueries {
    activityId?: string;
    wasteCodeId?: string;
    direction?: number;
    siteId?: string;
    quarryId?: string;
}

export const getAllDailyWastes = async (queries?: GetDailyWastesQueries): Promise<DailyWastesResponse> => {
    try {
        const response: AxiosResponse<DailyWastesResponse> = await apiClient.get('/daily-wastes', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getDailyWasteById = async (dailyWasteId: string): Promise<DailyWasteResponse> => {
    try {
        const response: AxiosResponse<DailyWasteResponse> = await apiClient.get('/daily-wastes/' + dailyWasteId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getDailyWastesByActivityId = async (activityId: string): Promise<DailyWastesResponse> => {
    try {
        const response: AxiosResponse<DailyWastesResponse> = await apiClient.get('/daily-wastes/activity/' + activityId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getDailyWastesByReportId = async (reportId: string): Promise<DailyWastesResponse> => {
    try {
        const response: AxiosResponse<DailyWastesResponse> = await apiClient.get('/daily-wastes/report/' + reportId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateDailyWasteBody {
    activityId: string;
    loadNatureId: string;
    wasteCodeId?: string;
    siteId?: string;
    quarryId?: string;
    companyId: string;
    direction: number;
    quantity?: number;
    unitPrice?: number;
}

export const createDailyWaste = async (data: CreateDailyWasteBody): Promise<DailyWasteResponse> => {
    try {
        const response: AxiosResponse<DailyWasteResponse> = await apiClient.post('/daily-wastes', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateDailyWasteBody {
    loadNatureId?: string;
    wasteCodeId?: string | null;
    siteId?: string | null;
    quarryId?: string | null;
    companyId?: string;
    direction?: number;
    quantity?: number;
    unitPrice?: number;
}

export const updateDailyWaste = async (dailyWasteId: string, data: UpdateDailyWasteBody): Promise<DailyWasteResponse> => {
    try {
        const response: AxiosResponse<DailyWasteResponse> = await apiClient.patch('/daily-wastes/' + dailyWasteId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const deleteDailyWaste = async (dailyWasteId: string): Promise<DailyWasteResponse> => {
    try {
        const response: AxiosResponse<DailyWasteResponse> = await apiClient.delete('/daily-wastes/' + dailyWasteId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
